import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from "axios";
import { useAuthStore } from "@/store/auth";

const API_BASE_URL = import.meta.env.VITE_API_URL || "/api";

export interface ApiError {
  error: {
    code: string;
    message: string;
    details?: Record<string, unknown>;
  };
}

export interface PaginationParams {
  page?: number;
  pageSize?: number;
}

export interface PaginatedResponse<T> {
  items: T[];
  pagination: {
    page: number;
    page_size: number;
    total_items: number;
    total_pages: number;
    has_next: boolean;
    has_previous: boolean;
  };
}

class ApiClient {
  private client: AxiosInstance;
  // Single in-flight refresh promise shared by every 401 handler. The
  // backend rotates the refresh token on every /auth/refresh call,
  // so if multiple 401s race they all try to spend the same token
  // and all but one get "Invalid refresh token" → logout. With this
  // promise, concurrent 401s await the same refresh and reuse its
  // result. Cleared as soon as the call settles.
  private refreshInFlight: Promise<{ accessToken: string; refreshToken: string }> | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: true,
    });

    this.setupInterceptors();
  }

  private async runRefresh(refreshToken: string) {
    if (!this.refreshInFlight) {
      this.refreshInFlight = (async () => {
        try {
          return await this.post<{ accessToken: string; refreshToken: string }>(
            "/v1/auth/refresh",
            { refreshToken },
          );
        } finally {
          // Release the lock once the call settles so the NEXT 401
          // wave gets a fresh attempt (with the rotated token).
          this.refreshInFlight = null;
        }
      })();
    }
    return this.refreshInFlight;
  }

  private setupInterceptors() {
    // Request interceptor - add auth token
    this.client.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const token = useAuthStore.getState().accessToken;
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error),
    );

    // Response interceptor - handle errors and token refresh
    this.client.interceptors.response.use(
      (response) => {
        // Track mutating API calls for activity logging
        const method = response.config.method?.toLowerCase();
        if (method && ["post", "put", "patch", "delete"].includes(method)) {
          const url = response.config.url || "";
          // Don't track the activity endpoint itself to avoid infinite loops
          if (!url.includes("/audit-logs/activity")) {
            // Lazy import to avoid circular dependency
            import("@/services/activity").then(({ activityTracker }) => {
              activityTracker.track({
                action: `api.${method}`,
                metadata: { url, status: response.status },
              });
            }).catch(() => {
              // Silently ignore import failures
            });
          }
        }
        return response;
      },
      async (error: AxiosError<ApiError>) => {
        const originalRequest = error.config;

        // Handle 401 - try to refresh token
        if (error.response?.status === 401 && originalRequest) {
          // Skip token refresh for auth endpoints that handle their own authentication
          const url = originalRequest.url || "";
          const isAuthEndpoint =
            url.includes("/auth/login") ||
            url.includes("/auth/mfa-verify") ||
            url.includes("/auth/refresh");

          if (
            !isAuthEndpoint &&
            !(originalRequest as InternalAxiosRequestConfig & { _retry?: boolean })._retry
          ) {
            (originalRequest as InternalAxiosRequestConfig & { _retry?: boolean })._retry = true;
            const { refreshToken, logout } = useAuthStore.getState();

            if (refreshToken) {
              try {
                // Share one refresh promise across all concurrent 401s
                // so we don't burn the rotated refresh token.
                const response = await this.runRefresh(refreshToken);

                const { setTokens, accessToken: currentAccess } = useAuthStore.getState();
                // Only one of the concurrent callers actually rotates
                // the store — the rest see that setTokens already
                // happened (current access token differs from the one
                // we sent) and skip the redundant write.
                if (currentAccess !== response.accessToken) {
                  setTokens(response.accessToken, response.refreshToken);
                }

                // Retry original request with new token
                if (originalRequest.headers) {
                  originalRequest.headers.Authorization = `Bearer ${response.accessToken}`;
                }
                return this.client(originalRequest);
              } catch {
                // Refresh failed - logout
                logout();
              }
            } else {
              logout();
            }
          }
        }

        return Promise.reject(this.formatError(error));
      },
    );
  }

  private formatError(error: AxiosError<ApiError>): ApiError & { response?: typeof error.response } {
    if (error.response?.data?.error) {
      return error.response.data;
    }

    const formatted: ApiError & { response?: typeof error.response } = {
      error: {
        code: error.response?.status === 422 ? "VALIDATION_ERROR" : "NETWORK_ERROR",
        message: error.message || "An unexpected error occurred",
      },
    };

    // Preserve original response for detailed error handling (e.g. 422 validation)
    if (error.response) {
      formatted.response = error.response;
    }

    return formatted;
  }

  async get<T>(url: string, params?: Record<string, unknown>): Promise<T> {
    const response = await this.client.get<T>(url, { params });
    return response.data;
  }

  async post<T>(url: string, data?: unknown, options?: { responseType?: "blob" | "json" }): Promise<T> {
    const response = await this.client.post<T>(url, data, {
      responseType: options?.responseType,
    });
    return response.data;
  }

  async put<T>(url: string, data?: unknown): Promise<T> {
    const response = await this.client.put<T>(url, data);
    return response.data;
  }

  async patch<T>(url: string, data?: unknown): Promise<T> {
    const response = await this.client.patch<T>(url, data);
    return response.data;
  }

  async delete<T>(url: string): Promise<T> {
    const response = await this.client.delete<T>(url);
    return response.data;
  }

  async getBlob(url: string): Promise<Blob> {
    const response = await this.client.get(url, { responseType: "blob" });
    return response.data;
  }

  async upload<T>(url: string, formData: FormData): Promise<T> {
    const response = await this.client.post<T>(url, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  }

  async streamSSE(
    url: string,
    data: unknown,
    callbacks: {
      onToken: (text: string) => void;
      onDone: () => void;
      onError: (msg: string) => void;
    },
    signal?: AbortSignal,
  ): Promise<void> {
    const token = useAuthStore.getState().accessToken;
    const baseUrl = this.client.defaults.baseURL || API_BASE_URL;
    const fullUrl = `${baseUrl}${url}`;

    const response = await fetch(fullUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(data),
      signal,
    });

    if (!response.ok) {
      callbacks.onError(`HTTP ${response.status}`);
      return;
    }

    const reader = response.body?.getReader();
    if (!reader) {
      callbacks.onError("No response body");
      return;
    }

    const decoder = new TextDecoder();
    let buffer = "";

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6).trim();
          if (payload === "[DONE]") {
            callbacks.onDone();
            return;
          }
          try {
            const parsed = JSON.parse(payload);
            if (typeof parsed === "string") {
              callbacks.onToken(parsed);
            } else if (parsed.error) {
              callbacks.onError(parsed.error);
              return;
            }
          } catch {
            // skip malformed lines
          }
        }
      }
      callbacks.onDone();
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      callbacks.onError(String(err));
    }
  }
}

export const api = new ApiClient();
export default api;
